// ════════════════════════════════════════════════════════════════
// FloorCost — Boot router
// ────────────────────────────────────────────────────────────────
// New entry point (index.html points here, not src/app.js). Three
// possible destinations:
//
//   /auth/callback  → consume the magic-link hash, redirect to /app
//   /app (anything) → lazy-load src/app.js if signed in,
//                     otherwise redirect to / for sign-in
//   anything else   → render the sign-in surface
//
// Lazy-loading src/app.js means the sign-in page ships ~5 kB of JS
// instead of the full ~120 kB app bundle. Visitors who never sign
// in never pay for the app.
//
// Routing is plain pathname matching — no router library. SPA
// fallback in netlify.toml ensures hard reloads on /app/* don't
// 404 against the static host.
// ════════════════════════════════════════════════════════════════

import { consumeCallbackHash, ensureRefreshTimer, isSignedIn, onAuthStateChange, getCurrentUserId } from '#features/auth/index.js';
import { APP_PREFIX, SIGN_IN_PATH, pickRoute } from '#lib/router.js';
import { renderSignIn } from '#features/auth/sign-in.js';
import { applyStorageNamespace } from '#lib/ns-storage.js';
import { installChunkReloadGuard, isChunkLoadError } from '#lib/chunk-recovery.js';

// Register the stale-chunk recovery FIRST — before the app.js import below
// or any lazy feature chunk can fail. A fresh deploy purges the hashed
// chunks this still-open session holds, so a dynamic import would 404 and
// render blank; this reloads once to pull the new shell. (See
// chunk-recovery.js.)
installChunkReloadGuard();

// ── Lazy app loader ──────────────────────────────────────────────
// Cached so a re-render (e.g. session change while on `/app`) doesn't
// re-import or re-boot the app module.
/** @type {Promise<void> | null} */
let _appLoaded = null;
function loadApp() {
  if (_appLoaded) return _appLoaded;
  _appLoaded = import('./app.js').then(() => {
    // app.js side-effect mounts itself via the existing <main>
    // structure declared in index.html. Nothing for us to do here.
  });
  return _appLoaded;
}

// ── Render the chosen surface ────────────────────────────────────

function paint() {
  // Consume any token-bearing hash regardless of which route we
  // landed on. Supabase's signup-confirmation template ignores the
  // API-supplied `email_redirect_to` and redirects to the project's
  // Site URL instead — for us that's /app, not /auth/callback. So
  // /app#access_token=...&type=signup needs to set the session before
  // the route picker runs, or pickRoute sees signedIn=false and
  // bounces the operator to /, discarding the tokens.
  // consumeCallbackHash() is a no-op when the hash is empty or
  // doesn't carry tokens, so unconditional call is safe. It also
  // self-guards against login-CSRF (SEC-A2): tokens are honoured only
  // when this browser requested a sign-in and no live session exists,
  // so "runs on every route" is not "trusts every link".
  consumeCallbackHash();

  const path = window.location.pathname;
  const signedIn = isSignedIn();
  const route = pickRoute(path, signedIn);
  const root = document.getElementById('boot-root');
  if (!root) {
    console.error('[boot] #boot-root missing from index.html');
    return;
  }

  switch (route) {
    case 'callback': {
      consumeCallbackHash();
      // Whether the consume succeeded or not, send the operator to
      // /app — if it failed, the next paint will detect no session
      // and redirect them back to /. This avoids an indefinite spin
      // on a malformed callback URL.
      window.history.replaceState({}, '', APP_PREFIX);
      paint();
      return;
    }
    case 'redirect-app': {
      window.history.replaceState({}, '', APP_PREFIX);
      paint();
      return;
    }
    case 'redirect-signin': {
      window.history.replaceState({}, '', SIGN_IN_PATH);
      paint();
      return;
    }
    case 'signin': {
      // The app shell may have already mounted in a previous paint
      // (race during sign-out). Hide it.
      _hideAppShell();
      renderSignIn(root);
      return;
    }
    case 'app': {
      // Hand the page over to the real app. The root container
      // becomes a no-op host; app.js mounts into the existing
      // <main class="app-main"> in index.html.
      root.innerHTML = '';
      _showAppShell();
      // S1-5: point the per-user work/queue keys at their (uid, store)
      // namespace BEFORE app.js's module graph evaluates (tracker/queue/
      // archive/templates alias STORAGE keys at import). The store token is
      // read RAW from the hint — importing store-context here would pull
      // stores.js → suburbs.js (~22 kB) into the 22 kB sign-in budget. Wrapped
      // so a namespacing bug degrades to shared base keys, never a blank shell.
      try {
        let storeHint = 'browns-plains'; // literal default; avoids the stores.js import (CRIT-2)
        try { storeHint = localStorage.getItem('fc_active_store') || 'browns-plains'; } catch { /* unavailable */ }
        const nsResult = applyStorageNamespace(getCurrentUserId(), storeHint);
        if (nsResult === 'switched') {
          // A DIFFERENT operator signed in without a page reload (in-page
          // session swap on a shared counter — auth/queue call setSession(null)
          // with no navigation, then the next operator signs in here). The
          // module graph + in-memory signals are still bound to the previous
          // operator; only a reload re-initialises everything cleanly under the
          // new namespace. STORAGE was intentionally left untouched.
          window.location.reload();
          return;
        }
      } catch { /* non-fatal — leaves shared base keys */ }
      loadApp().catch((err) => {
        // A stale app-shell chunk rejects here even though the global guard is
        // already reloading to the fresh shell (chunk-recovery.js deliberately
        // does NOT preventDefault). Don't paint the failure message over the
        // in-flight reload — it would flash for the sub-second before the
        // navigation commits.
        if (isChunkLoadError(err)) return;
        console.error('[boot] app load failed:', err);
        root.innerHTML = '<p style="padding:40px;text-align:center;color:#cc2929">FloorCost failed to load. Reload the page or check your connection.</p>';
      });
      return;
    }
  }
}

// Hide / show the app chrome (header + shell) as a unit. The
// header lives outside `.app-shell` in index.html because the
// shell is a horizontal flex container (rail + main) and the
// header is a full-width sticky bar above it; both belong to the
// authenticated app and should never appear on the sign-in
// surface.
function _hideAppShell() {
  const header = document.querySelector('.header');
  if (header instanceof HTMLElement) header.style.display = 'none';
  const shell = document.querySelector('.app-shell');
  if (shell instanceof HTMLElement) shell.style.display = 'none';
}
function _showAppShell() {
  const header = document.querySelector('.header');
  if (header instanceof HTMLElement) header.style.display = '';
  const shell = document.querySelector('.app-shell');
  if (shell instanceof HTMLElement) shell.style.display = '';
}

// ── Wire up ──────────────────────────────────────────────────────

// Re-paint on browser navigation (back/forward).
window.addEventListener('popstate', paint);

// Re-paint on auth state change (sign-in / sign-out).
onAuthStateChange(() => paint());

// PR-3: kick off the background access-token refresh timer if a
// session is already in storage. setSession also re-schedules on
// every subsequent sign-in / refresh.
ensureRefreshTimer();

// First paint. Defer one tick so the DOM is fully parsed and any
// inline scripts in index.html have settled.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', paint, { once: true });
} else {
  paint();
}
